import { useEffect, useMemo, useState } from "react";
import { isVisible } from "./helpers";

export type ProxyAction = {
  visible: boolean
  disabled: boolean
  act: () => void
}

/**
 * This hook is used to watch a button or link provided by getElement. It watches for visibility and disable.
 * You can also trigger the action.
 * @typeParam T - Type of object returned by getElement
 * @param getElement - Function to get the element to proxy
 * @returns a memoized proxy action
 */
export function useProxyAction<T extends HTMLElement>(getElement: () => T): ProxyAction {
  const [visible, setVisible] = useState(false);
  const [disabled, setDisabled] = useState(false);
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        const element = mutation.target as T;
        setVisible(isVisible(element));
        setDisabled(element.getAttribute('aria-disabled') === 'true');
      }
    });

    const element = getElement();
    observer.observe(element, { attributes: true, attributeFilter: ['aria-hidden', 'aria-disabled'] });
    setVisible(isVisible(element));
    setDisabled(element.getAttribute('aria-disabled') === 'true');

    return () => observer.disconnect();
  }, [getElement]);
  return useMemo(() => ({
    visible,
    disabled,
    act: () => getElement().click(),
  }), [disabled, getElement, visible]);
}
